import React from 'react'
import { Box, ToggleButton, Tooltip, Typography, Chip } from '@mui/material'
import DrawIcon from '@mui/icons-material/Draw'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

interface ToolBarProps {
  isDrawingMode: boolean
  isEditMode: boolean
  showRedactions: boolean
  allImagesLoaded: boolean
  loadedImageCount: number
  totalImagesInSeries: number
  hasCurrentSeriesMasks: boolean
  onToggleDrawingMode: () => void
  onToggleEditMode: (event: React.MouseEvent<HTMLElement>, value: boolean) => void
  onToggleShowRedactions: () => void
}

export function ToolBar({
  isDrawingMode,
  isEditMode,
  showRedactions,
  allImagesLoaded,
  loadedImageCount,
  totalImagesInSeries,
  hasCurrentSeriesMasks,
  onToggleDrawingMode,
  onToggleEditMode,
  onToggleShowRedactions,
}: ToolBarProps) {
  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title={isDrawingMode ? 'Cancel drawing' : 'Draw rectangle to mark PHI'}>
            <ToggleButton
              value="draw"
              selected={isDrawingMode}
              onChange={onToggleDrawingMode}
              color="primary"
              sx={{ borderRadius: '4px' }}
              disabled={isEditMode || !allImagesLoaded}
            >
              <DrawIcon />
            </ToggleButton>
          </Tooltip>

          <Tooltip title={isEditMode ? 'Exit edit mode' : 'Edit PHI boxes'}>
            <ToggleButton
              value="edit"
              selected={isEditMode}
              onChange={onToggleEditMode}
              color="primary"
              sx={{ borderRadius: '4px' }}
              disabled={isDrawingMode || !allImagesLoaded}
            >
              <EditIcon />
            </ToggleButton>
          </Tooltip>

          {/* Show mode indicator messages */}
          {isDrawingMode && (
            <Typography variant="body2" color="text.secondary">
              Drawing PHI Box - Click and drag on the image
            </Typography>
          )}

          {/* Show loading status when not all images are loaded yet */}
          {!allImagesLoaded && !isDrawingMode && !isEditMode && (
            <Chip
              label={`Loading in progress: ${loadedImageCount}/${totalImagesInSeries} images`}
              color="primary"
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {hasCurrentSeriesMasks && (
          <Tooltip title={showRedactions ? 'Hide PHI redactions' : 'Show PHI redactions'}>
            <ToggleButton
              value="visibility"
              selected={showRedactions}
              onChange={onToggleShowRedactions}
              color="primary"
              sx={{ borderRadius: '4px' }}
              disabled={isDrawingMode || !allImagesLoaded}
            >
              {showRedactions ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </ToggleButton>
          </Tooltip>
        )}
      </Box>
    </>
  )
}
